/*
 * Obsidian Workwear — automatic logo setup fee.
 * Adds the "Logo setup fee" (one per order) when the basket is under £200
 * and contains no packs (packs include setup). Removes it otherwise.
 * Customers tagged "logo-on-file" never pay it while logged in
 * (window.ObsidianFee is set in snippets/skip-to-content-link.liquid).
 *
 * Also keeps print add-ons in step with the garments: every cart line with the
 * property "Sleeve print", "Back of neck print" or "Large front print" set to Yes needs one matching
 * add-on per garment, so the add-on line quantities always equal those totals.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const FEE_VARIANT = 55518440489289;
const FREE_FROM = 20000; // pence, inc VAT
const PACK_TYPE = 'Packs';
const ADDONS = [
  { prop: 'Sleeve print', variant: 55518850974025 },
  { prop: 'Back of neck print', variant: 55518851006793 },
  { prop: 'Large front print', variant: 55518864539977 },
];
const ADDON_IDS = new Set(ADDONS.map((a) => a.variant));
const isExtra = (i) => i.variant_id === FEE_VARIANT || ADDON_IDS.has(i.variant_id);

const nativeFetch = window.fetch.bind(window);
let running = null;
let again = false;
let dispatching = false;

const cartJson = () =>
  nativeFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

const post = (url, body) =>
  nativeFetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());

const state = () => window.ObsidianFee || {};
let feeInCart = false;

function feeNeeded(cart) {
  if (state().logoOnFile) return false; // returning customer, logo already on file
  const garments = cart.items.filter((i) => !isExtra(i));
  if (!garments.length) return false;
  if (garments.some((i) => i.product_type === PACK_TYPE)) return false;
  const subtotal = cart.items
    .filter((i) => i.variant_id !== FEE_VARIANT)
    .reduce((sum, i) => sum + i.final_line_price, 0);
  return subtotal < FREE_FROM;
}

function refreshCartUI(cart, need) {
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    dispatching = true;
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: need ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: String(FEE_VARIANT), quantity: need ? 1 : 0 }],
        promise: deferred.promise,
      })
    );
    dispatching = false;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'setup-fee', didError: false },
    });
  } catch (error) {
    dispatching = false;
    console.warn('[setup-fee] UI refresh failed', error);
    if (window.location.pathname.startsWith('/cart')) window.location.reload();
  }
}

const qtyOf = (cart, variant) =>
  cart.items.filter((i) => i.variant_id === variant).reduce((s, i) => s + i.quantity, 0);

async function syncAddons(cart) {
  const toAdd = [];
  const updates = {};
  for (const a of ADDONS) {
    const want = cart.items
      .filter((i) => !isExtra(i) && i.properties && i.properties[a.prop] === 'Yes')
      .reduce((s, i) => s + i.quantity, 0);
    const have = qtyOf(cart, a.variant);
    if (want === have) continue;
    if (have === 0) toAdd.push({ id: a.variant, quantity: want });
    else updates[a.variant] = want;
  }
  if (toAdd.length) await post('/cart/add.js', { items: toAdd });
  if (Object.keys(updates).length) await post('/cart/update.js', { updates });
  return toAdd.length > 0 || Object.keys(updates).length > 0;
}

async function sync() {
  let cart = await cartJson();
  const addonsChanged = await syncAddons(cart);
  if (addonsChanged) cart = await cartJson();

  const need = feeNeeded(cart);
  const qty = qtyOf(cart, FEE_VARIANT);
  feeInCart = need;
  hasItems = cart.items.some((i) => !isExtra(i));
  renderNotes();

  let feeChanged = true;
  if (need && qty === 0) await post('/cart/add.js', { items: [{ id: FEE_VARIANT, quantity: 1 }] });
  else if (need && qty !== 1) await post('/cart/update.js', { updates: { [FEE_VARIANT]: 1 } });
  else if (!need && qty > 0) await post('/cart/update.js', { updates: { [FEE_VARIANT]: 0 } });
  else feeChanged = false;

  if (!addonsChanged && !feeChanged) return false;
  refreshCartUI(await cartJson(), need);
  return true;
}

// Basket messages next to every checkout button (cart page and drawer).
let hasItems = false;

function noteHtml() {
  const s = state();
  if (!hasItems) return '';
  if (s.logoOnFile) return "Your logo's on file, so there's no setup fee on this order.";
  if (feeInCart && !s.loggedIn) {
    const base = s.loginUrl || '/account/login';
    const url = `${base}${base.includes('?') ? '&' : '?'}return_to=${encodeURIComponent('/cart')}`;
    return `Ordered with us before? <a href="${url}">Log in</a> and the setup fee comes off.`;
  }
  return '';
}

function renderNotes() {
  const html = noteHtml();
  for (const button of document.querySelectorAll('button[name="checkout"], #checkout')) {
    const anchor = button.closest('form') || button.parentElement;
    if (!anchor || !anchor.parentElement) continue;
    let note = anchor.previousElementSibling;
    if (!note || !note.classList.contains('obsidian-fee-note')) {
      if (!html) continue;
      note = document.createElement('p');
      note.className = 'obsidian-fee-note';
      anchor.parentElement.insertBefore(note, anchor);
    }
    if (!html) {
      note.remove();
    } else if (note.innerHTML !== html) {
      note.innerHTML = html;
    }
  }
}

let notesQueued = false;
new MutationObserver(() => {
  if (notesQueued) return;
  notesQueued = true;
  setTimeout(() => {
    notesQueued = false;
    renderNotes();
  }, 30);
}).observe(document.documentElement, { childList: true, subtree: true });

function schedule() {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    try {
      do {
        again = false;
        await sync();
      } while (again);
    } catch (error) {
      console.warn('[setup-fee]', error);
    } finally {
      running = null;
    }
  })();
  return running;
}

// Re-check after any cart change made by the theme or apps.
window.fetch = async (...args) => {
  const response = await nativeFetch(...args);
  try {
    const url = String(args[0] instanceof Request ? args[0].url : args[0]);
    if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
      response
        .clone()
        .text()
        .finally(() => setTimeout(schedule, 50));
    }
  } catch (_) {
    /* ignore */
  }
  return response;
};

for (const name of Object.values(StandardEvents || {})) {
  if (typeof name !== 'string' || !/cart/i.test(name)) continue;
  document.addEventListener(name, (event) => {
    if (dispatching) return;
    Promise.resolve(event.promise)
      .catch(() => {})
      .then(() => setTimeout(schedule, 50));
  });
}

// Make sure the basket is right before checkout.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    const submitter = event.submitter;
    const goingToCheckout =
      (submitter && submitter.name === 'checkout') || (form && /\/checkout/.test(form.getAttribute('action') || ''));
    if (!goingToCheckout) return;
    event.preventDefault();
    schedule().finally(() => {
      window.location.href = '/checkout';
    });
  },
  true
);

schedule();
